/**
 * Stand-in for the analytics backend until the real endpoints land. Everything here is
 * generated from a fixed seed so the page renders the same numbers on every reload.
 *
 * Underlying fixture is one daily activity record per task group for every day in 2026.
 * `buildMockCostGroups` / `buildMockCostSeries` filter those days by the query's date
 * range, task types, and manager email, then aggregate — the same shape the real
 * endpoints will apply over completed tasks.
 */
import dayjs from 'dayjs';
import {
  TaskType,
  taskTypeZod,
} from 'packages/lib/api/annotations/contract/enums';
import {
  CostGroupItem,
  CostGroupResponse,
  CostMetric,
  CostQuery,
  CostSeriesPoint,
  CostSeriesQuery,
  CostSeriesResponse,
} from 'packages/lib/api/annotations-ops/contract';

// 2026-08-04 Used Cursor (Opus 5) to generate this temporary mock data (to be removed once backend API is available)

/** Sample group the Analytics > Pipelines nav item points at until pipelines are wired up. */
export const DEMO_SAMPLE_GROUP_TOKEN = 'b9558f7d82fe43998c2d70de555e2191';

const TASK_GROUP_COUNT = 100;
const MOCK_YEAR = 2026;
const DAYS_IN_YEAR =
  dayjs(`${MOCK_YEAR}-12-31`).diff(dayjs(`${MOCK_YEAR}-01-01`), 'day') + 1;

/** Illustrative: task types judged per scene or object rather than per frame. */
const FRAMELESS_TASK_TYPES: TaskType[] = [
  'Attribute',
  'ImageEnrichment',
  'SceneEnrichment',
  'TrackletClassification',
];

/** mulberry32 — small deterministic PRNG so the fixtures never change between renders. */
const createRandom = (seed: number): (() => number) => {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const random = createRandom(20260630);

const pick = <T>(values: readonly T[]): T =>
  values[Math.floor(random() * values.length)];

const between = (min: number, max: number): number =>
  min + random() * (max - min);

const money = (value: number): number => Math.round(value * 100) / 100;

const hours = (value: number): number => Math.round(value * 10) / 10;

const hexToken = (): string =>
  Array.from({ length: 32 }, () => Math.floor(random() * 16).toString(16)).join(
    ''
  );

const SUBJECTS = [
  'Traffic Light Detection',
  'Pedestrian Crossing',
  'Highway Merge',
  'Construction Zone',
  'Driverless Route',
  'Night Driving',
  'Heavy Rain',
  'Cyclist Interaction',
  'Roundabout Navigation',
  'Parking Lot Maneuver',
] as const;
const MODALITIES = ['Video', 'Lidar', 'Image', 'Multiview', 'Audio'] as const;

/** One day of completed work under a task group. */
interface MockDayActivity {
  /** YYYY-MM-DD */
  date: string;
  numTasks: number;
  numQATasks: number;
  numAuditTasks: number;
  numFrames: number | null;
  numAnnotations: number;
  labelingCost: number;
  qaCost: number;
  auditCost: number;
  scaleCost: number;
  labelingHours: number;
  qaHours: number;
  auditHours: number;
}

/** Per-group constants that shape every one of that group's days. */
interface MockGroupProfile {
  /** Baseline weekday labeling spend for this group. */
  dailyLabelingBase: number;
  qaRatio: number;
  auditRatio: number;
  scaleRatio: number;
  costPerTask: number;
  framesPerTask: number | null;
  annotationsPerTask: number;
  /** Turns spend into hours worked, since the fixture has no timesheets. */
  hourlyRate: number;
  /** Share of labeled tasks that also reach QA / audit on the same day. */
  qaTaskShare: number;
  auditTaskShare: number;
}

interface MockTaskGroup {
  token: string;
  name: string;
  taskType: TaskType;
  /**
   * Stand-in for the `managerEmail` filter. The fixture has no org chart, so any
   * manager email matches this deterministic subset rather than real direct reports.
   */
  isDirectReportWork: boolean;
  /** Activity for every calendar day in {@link MOCK_YEAR}. */
  days: MockDayActivity[];
}

const createDayActivity = (
  date: string,
  profile: MockGroupProfile
): MockDayActivity => {
  const { framesPerTask, hourlyRate } = profile;
  const weekday = dayjs(date).day();
  const isWeekend = weekday === 0 || weekday === 6;
  // Weekends are quieter; some weekdays are idle so shorter windows can drop a group.
  const activeChance = isWeekend ? 0.25 : 0.85;
  if (random() > activeChance) {
    return {
      date,
      numTasks: 0,
      numQATasks: 0,
      numAuditTasks: 0,
      numFrames: framesPerTask === null ? null : 0,
      numAnnotations: 0,
      labelingCost: 0,
      qaCost: 0,
      auditCost: 0,
      scaleCost: 0,
      labelingHours: 0,
      qaHours: 0,
      auditHours: 0,
    };
  }

  const intensity = isWeekend ? between(0.2, 0.6) : between(0.5, 1.4);
  const labelingCost = money(profile.dailyLabelingBase * intensity);
  const qaCost = money(labelingCost * profile.qaRatio);
  const auditCost = money(labelingCost * profile.auditRatio);
  const numTasks = Math.max(1, Math.round(labelingCost / profile.costPerTask));
  const numFrames = framesPerTask === null ? null : numTasks * framesPerTask;
  const numAnnotations = numTasks * profile.annotationsPerTask;

  return {
    date,
    numTasks,
    numQATasks: Math.round(numTasks * profile.qaTaskShare),
    numAuditTasks: Math.round(numTasks * profile.auditTaskShare),
    numFrames,
    numAnnotations,
    labelingCost,
    qaCost,
    auditCost,
    scaleCost: money(labelingCost * profile.scaleRatio),
    labelingHours: hours(labelingCost / hourlyRate),
    qaHours: hours(qaCost / hourlyRate),
    auditHours: hours(auditCost / hourlyRate),
  };
};

const createTaskGroup = (): MockTaskGroup => {
  const taskType = pick(taskTypeZod.options);
  const hasFrames = !FRAMELESS_TASK_TYPES.includes(taskType);
  // Annual labeling for a group lands in the low hundreds to tens of thousands;
  // spread across ~220 active weekdays that comes out to a few dollars to ~$100/day.
  const annualLabeling = between(800, 28000);
  const profile: MockGroupProfile = {
    dailyLabelingBase: annualLabeling / 220,
    qaRatio: between(0.15, 0.4),
    auditRatio: between(0.1, 0.32),
    // ~50/50 whether iMerit labeling beats Scale or costs more.
    scaleRatio: random() < 0.5 ? between(0.55, 0.95) : between(1.1, 2.1),
    costPerTask: between(1.5, 14),
    framesPerTask: hasFrames ? Math.round(between(1, 6)) : null,
    annotationsPerTask: Math.round(between(2, 40)),
    hourlyRate: between(8, 22),
    qaTaskShare: between(0.3, 0.9),
    auditTaskShare: between(0.1, 0.5),
  };

  const yearStart = dayjs(`${MOCK_YEAR}-01-01`);
  const days: MockDayActivity[] = Array.from(
    { length: DAYS_IN_YEAR },
    (_, index) =>
      createDayActivity(
        yearStart.add(index, 'day').format('YYYY-MM-DD'),
        profile
      )
  );

  return {
    token: hexToken(),
    name: `${MOCK_YEAR} ${pick(SUBJECTS)} (${pick(MODALITIES)}) v${Math.round(
      between(1, 4)
    )} Task Group`,
    taskType,
    isDirectReportWork: random() < 0.4,
    days,
  };
};

const MOCK_TASK_GROUPS: MockTaskGroup[] = Array.from(
  { length: TASK_GROUP_COUNT },
  createTaskGroup
);

const matchesQueryFilters = (group: MockTaskGroup, query: CostQuery): boolean =>
  (query.taskTypes.length === 0 || query.taskTypes.includes(group.taskType)) &&
  (!query.managerEmail || group.isDirectReportWork);

const daysInRange = (
  group: MockTaskGroup,
  startDate: string,
  endDate: string
): MockDayActivity[] =>
  group.days.filter(
    ({ date, numTasks }) => numTasks > 0 && date >= startDate && date <= endDate
  );

const aggregateGroup = (
  group: MockTaskGroup,
  activeDays: MockDayActivity[]
): CostGroupItem | null => {
  if (activeDays.length === 0) return null;

  const summedNumTasks = activeDays.reduce(
    (total, day) => total + day.numTasks,
    0
  );
  const summedNumFrames = activeDays.reduce(
    (total, day) => total + (day.numFrames ?? 0),
    0
  );
  const summedNumAnnotations = activeDays.reduce(
    (total, day) => total + day.numAnnotations,
    0
  );
  const summediMeritLabelingCost = money(
    activeDays.reduce((total, day) => total + day.labelingCost, 0)
  );
  const hasFrames = !FRAMELESS_TASK_TYPES.includes(group.taskType);

  return {
    token: group.token,
    name: group.name,
    taskType: group.taskType,
    avgLabelingCostPerTask: money(summediMeritLabelingCost / summedNumTasks),
    avgLabelingCostPerFrame:
      hasFrames && summedNumFrames > 0
        ? money(summediMeritLabelingCost / summedNumFrames)
        : null,
    avgLabelingCostPerAnnotation:
      summedNumAnnotations > 0
        ? money(summediMeritLabelingCost / summedNumAnnotations)
        : 0,
    summediMeritLabelingCost,
    summediMeritQACost: money(
      activeDays.reduce((total, day) => total + day.qaCost, 0)
    ),
    summediMeritAuditCost: money(
      activeDays.reduce((total, day) => total + day.auditCost, 0)
    ),
    summedScaleCost: money(
      activeDays.reduce((total, day) => total + day.scaleCost, 0)
    ),
    summedNumTasks,
    summedNumAnnotations,
    summedNumFrames: hasFrames ? summedNumFrames : null,
  };
};

/**
 * Mirrors `GET /cost/group/{token}`: keep task groups matching taskType / managerEmail,
 * aggregate only days inside startDate–endDate, and drop groups with no in-range work.
 */
export const buildMockCostGroups = (query: CostQuery): CostGroupResponse => ({
  data: MOCK_TASK_GROUPS.filter((group) => matchesQueryFilters(group, query))
    .map((group) =>
      aggregateGroup(group, daysInRange(group, query.startDate, query.endDate))
    )
    .filter((item): item is CostGroupItem => item !== null),
});

/** One day of activity summed across every task group that matched the filters. */
type DailyTotals = Omit<MockDayActivity, 'date' | 'numFrames'>;

const emptyTotals = (): DailyTotals => ({
  numTasks: 0,
  numQATasks: 0,
  numAuditTasks: 0,
  numAnnotations: 0,
  labelingCost: 0,
  qaCost: 0,
  auditCost: 0,
  scaleCost: 0,
  labelingHours: 0,
  qaHours: 0,
  auditHours: 0,
});

/**
 * How each metric turns a day's totals into a point. Comparison swaps the role
 * breakdown for iMerit labeling spend against the industry (Scale) benchmark.
 */
const METRIC_POINT: Record<
  CostMetric,
  (date: string, totals: DailyTotals) => CostSeriesPoint
> = {
  totalCost: (date, totals) => ({
    date,
    labeling: money(totals.labelingCost),
    qa: money(totals.qaCost),
    audit: money(totals.auditCost),
  }),
  totalTasks: (date, totals) => ({
    date,
    labeling: totals.numTasks,
    qa: totals.numQATasks,
    audit: totals.numAuditTasks,
  }),
  totalHours: (date, totals) => ({
    date,
    labeling: hours(totals.labelingHours),
    qa: hours(totals.qaHours),
    audit: hours(totals.auditHours),
  }),
  comparison: (date, totals) => ({
    date,
    labeling: money(totals.labelingCost),
    industry: money(totals.scaleCost),
  }),
};

/**
 * Mirrors `GET /cost/group/{token}/time-series`: one point per day in the requested
 * range, summing the same filtered daily activity that feeds the table. Days outside
 * 2026 (or with no matching activity) still appear as zeroes so the chart axis matches
 * the picker. The fixture only records completed work, so unlike the real endpoint its
 * hours exclude in-progress tasks.
 */
export const buildMockCostSeries = (
  query: CostSeriesQuery
): CostSeriesResponse => {
  const start = dayjs(query.startDate);
  const dayCount = Math.max(1, dayjs(query.endDate).diff(start, 'day') + 1);
  const groups = MOCK_TASK_GROUPS.filter((group) =>
    matchesQueryFilters(group, query)
  );
  const toPoint = METRIC_POINT[query.metric];

  // date -> day-index in each group's `days` array (only valid inside MOCK_YEAR).
  const yearStart = dayjs(`${MOCK_YEAR}-01-01`);

  return {
    data: Array.from({ length: dayCount }, (_, index) => {
      const date = start.add(index, 'day').format('YYYY-MM-DD');
      const dayOfYear = dayjs(date).diff(yearStart, 'day');
      const inMockYear = dayOfYear >= 0 && dayOfYear < DAYS_IN_YEAR;
      const totals = emptyTotals();

      if (inMockYear) {
        for (const group of groups) {
          const day = group.days[dayOfYear];
          totals.numTasks += day.numTasks;
          totals.numQATasks += day.numQATasks;
          totals.numAuditTasks += day.numAuditTasks;
          totals.numAnnotations += day.numAnnotations;
          totals.labelingCost += day.labelingCost;
          totals.qaCost += day.qaCost;
          totals.auditCost += day.auditCost;
          totals.scaleCost += day.scaleCost;
          totals.labelingHours += day.labelingHours;
          totals.qaHours += day.qaHours;
          totals.auditHours += day.auditHours;
        }
      }

      return toPoint(date, totals);
    }),
  };
};
